import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';

import { ApiService } from '../../core/api.service';
import { I18nService } from '../../core/i18n.service';
import { DashboardCountItem, ProcurementTypeDashboard, RequisitionBudgetHeadAmount, RequisitionDashboardData, RequisitionDashboardStatus } from '../../core/models';
import { PaginationComponent } from '../../core/pagination.component';
import { LocalCurrencyPipe, LocalDatePipe, LocalNumberPipe, TranslatePipe } from '../../core/translate.pipe';

@Component({
  selector: 'app-requisition-dashboard',
  standalone: true,
  imports: [CommonModule, LocalCurrencyPipe, LocalDatePipe, LocalNumberPipe, PaginationComponent, RouterLink, TitleCasePipe, TranslatePipe],
  template: `
    <section class="req-dashboard" *ngIf="data; else loadingState">
      <header class="req-dashboard-head">
        <div>
          <span class="new-page-tag">{{ 'nav.dashboard' | t }}</span>
          <h1>{{ 'reqDashboard.title' | t }}</h1>
          <p>{{ 'reqDashboard.description' | t }}</p>
        </div>
        <div class="req-dashboard-actions">
          <button type="button" class="secondary" (click)="load()" [disabled]="loading">{{ 'dashboard.refreshAll' | t }}</button>
          <button type="button" (click)="print()">{{ 'common.print' | t }}</button>
        </div>
      </header>

      <p class="error" *ngIf="error">{{ error }}</p>

      <section class="req-status-grid" aria-label="Requisition status indicators">
        <button
          type="button"
          class="req-status-tile"
          *ngFor="let item of visibleStatuses"
          [class.active]="activeStatus?.key === item.key"
          [class.status-warning]="item.key === 'pending'"
          [class.status-success]="item.key === 'approved'"
          [class.status-danger]="item.key === 'rejected'"
          (click)="selectStatus(item)">
          <span>{{ item.label | t }}</span>
          <strong>{{ item.count | ln:'1.0-0' }}</strong>
          <small>{{ item.description | t }}</small>
        </button>
      </section>

      <section class="req-dashboard-layout">
        <article class="panel req-insight-panel">
          <div class="section-heading compact-heading">
            <div>
              <h2>{{ activeStatus?.label | t }}</h2>
              <p>{{ activeStatus?.description | t }}</p>
            </div>
          </div>

          <div class="req-primary-metric">
            <div>
              <span>{{ 'reqDashboard.selectedStatus' | t }}</span>
              <strong>{{ (activeStatus?.count || 0) | ln:'1.0-0' }}</strong>
            </div>
            <div>
              <span>{{ 'reqDashboard.shareOfTotal' | t }}</span>
              <strong>{{ statusShare | ln:'1.0-1' }}%</strong>
            </div>
          </div>

          <div class="req-message">
            <strong>{{ insightTitle | t }}</strong>
            <p>{{ insightText | t }}</p>
          </div>

          <a class="button req-open-register" routerLink="/lifecycle/requisitions">{{ 'common.viewRegister' | t }}</a>
        </article>

        <article class="panel req-budget-panel">
          <div class="section-heading compact-heading">
            <div>
              <h2>{{ 'reqDashboard.budgetHeadAmounts' | t }}</h2>
              <p>{{ 'reqDashboard.budgetHeadDescription' | t }}</p>
            </div>
            <div class="total-entries compact-total">
              <strong>{{ data.summary.total_amount | lcurrency:'INR':'1.2-2' }}</strong>
              <span>{{ 'reqDashboard.totalRequisitionValue' | t }}</span>
            </div>
          </div>

          <div class="req-budget-list">
            <button
              type="button"
              class="req-budget-row"
              *ngFor="let item of pagedBudgetHeads"
              [class.active]="activeBudget?.budget_head === item.budget_head"
              (click)="selectBudget(item)">
              <div>
                <strong>{{ item.budget_head | t }}</strong>
                <span>{{ item.count | ln:'1.0-0' }} {{ 'common.records' | t }}</span>
              </div>
              <div class="req-budget-amount">
                <strong>{{ item.amount | lcurrency:'INR':'1.2-2' }}</strong>
                <span>{{ budgetShare(item.amount) | ln:'1.0-1' }}%</span>
              </div>
              <div class="req-budget-track">
                <span [style.width.%]="budgetShare(item.amount)"></span>
              </div>
            </button>
          </div>
          <app-pagination
            [total]="data.budget_heads.length"
            [page]="budgetPage"
            [pageSize]="budgetPageSize"
            [pageSizes]="cardPageSizes"
            (pageChange)="budgetPage = $event"
            (pageSizeChange)="changeBudgetPageSize($event)">
          </app-pagination>
        </article>
      </section>

      <section class="panel req-payment-panel">
        <div class="section-heading compact-heading">
          <div>
            <h2>{{ 'Vendor Payment Status' | t }}</h2>
            <p>{{ 'Latest vendor invoices, payments and outstanding balances.' | t }}</p>
          </div>
          <a class="button" routerLink="/reports">{{ 'View Payment Report' | t }}</a>
        </div>
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{{ 'Vendor' | t }}</th>
                <th>{{ 'PO No' | t }}</th>
                <th>{{ 'Invoice No' | t }}</th>
                <th>{{ 'Invoice Date' | t }}</th>
                <th>{{ 'Invoice Amount' | t }}</th>
                <th>{{ 'Paid Amount' | t }}</th>
                <th>{{ 'Outstanding Amount' | t }}</th>
                <th>{{ 'Payment Status' | t }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of data.vendor_payment_status">
                <td>{{ item.vendor_name }}</td>
                <td>{{ item.po_no }}</td>
                <td>{{ item.invoice_no | t }}</td>
                <td>{{ item.invoice_date ? (item.invoice_date | ldate) : '-' }}</td>
                <td>{{ item.invoice_amount | lcurrency:'INR':'1.2-2' }}</td>
                <td>{{ item.paid_amount | lcurrency:'INR':'1.2-2' }}</td>
                <td>{{ item.outstanding_amount | lcurrency:'INR':'1.2-2' }}</td>
                <td><span class="status">{{ item.payment_status | t }}</span></td>
              </tr>
              <tr *ngIf="!data.vendor_payment_status?.length">
                <td colspan="8" class="report-empty">{{ 'No vendor payment records are available.' | t }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="panel procurement-type-dashboard" *ngIf="procurementTypes.length">
        <div class="section-heading compact-heading">
          <div>
            <h2>{{ 'procTypeDashboard.title' | t }}</h2>
            <p>{{ 'procTypeDashboard.description' | t }}</p>
          </div>
        </div>

        <div class="proc-type-tabs">
          <button
            type="button"
            class="secondary"
            *ngFor="let item of procurementTypes"
            [class.active]="activeProcurementType?.key === item.key"
            (click)="selectProcurementType(item)">
            <span>{{ item.label | t }}</span>
            <strong>{{ item.total_cases | ln:'1.0-0' }}</strong>
          </button>
        </div>

        <ng-container *ngIf="activeProcurementType as procurementType">
          <section class="proc-indicator-grid">
            <article class="proc-indicator">
              <span>{{ 'procTypeDashboard.bidPublication' | t }}</span>
              <strong>{{ publishedCount(procurementType) | ln:'1.0-0' }}</strong>
              <p>{{ 'procTypeDashboard.bidPublicationMessage' | t }}</p>
            </article>
            <article class="proc-indicator">
              <span>{{ 'procTypeDashboard.submissionDate' | t }}</span>
              <strong>{{ submittedDateCount(procurementType) | ln:'1.0-0' }}</strong>
              <p>{{ 'procTypeDashboard.submissionDateMessage' | t }}</p>
            </article>
            <article class="proc-indicator">
              <span>{{ 'procTypeDashboard.emdStatus' | t }}</span>
              <div class="mini-status-list">
                <div *ngFor="let item of procurementType.emd_status">
                  <span>{{ item.label | t }}</span>
                  <strong>{{ item.count | ln:'1.0-0' }}</strong>
                </div>
              </div>
            </article>
            <article class="proc-indicator">
              <span>{{ 'procTypeDashboard.bidStatus' | t }}</span>
              <div class="mini-status-list">
                <div *ngFor="let item of procurementType.bid_status">
                  <span>{{ item.label | t }}</span>
                  <strong>{{ item.count | ln:'1.0-0' }}</strong>
                </div>
              </div>
            </article>
            <article class="proc-indicator">
              <span>{{ 'procTypeDashboard.purchaseOrder' | t }}</span>
              <div class="mini-status-list">
                <div *ngFor="let item of procurementType.purchase_order_status">
                  <span>{{ item.label | t }}</span>
                  <strong>{{ item.count | ln:'1.0-0' }}</strong>
                </div>
              </div>
            </article>
            <article class="proc-indicator">
              <span>{{ 'procTypeDashboard.deliveryAcceptance' | t }}</span>
              <div class="mini-status-list">
                <div *ngFor="let item of nonEmptyCounts(procurementType.delivery_acceptance_status)">
                  <span>{{ item.label | t }}</span>
                  <strong>{{ item.count | ln:'1.0-0' }}</strong>
                </div>
              </div>
            </article>
            <article class="proc-indicator">
              <span>{{ 'procTypeDashboard.performanceSecurity' | t }}</span>
              <div class="mini-status-list">
                <div *ngFor="let item of procurementType.performance_security_status">
                  <span>{{ item.label | t }}</span>
                  <strong>{{ item.count | ln:'1.0-0' }}</strong>
                </div>
              </div>
            </article>
          </section>

          <section class="proc-communicator">
            <div>
              <strong>{{ procurementType.label | t }} {{ 'procTypeDashboard.communicationTitle' | t }}</strong>
              <p>{{ procurementTypeMessage(procurementType) | t }}</p>
            </div>
            <a class="button" [routerLink]="procurementTypeRoute(procurementType.key)">{{ 'common.viewRegister' | t }}</a>
          </section>

          <div class="table-scroll proc-case-table">
            <table>
              <thead>
                <tr>
                  <th>{{ 'Procurement Case No' | t }}</th>
                  <th>{{ 'procTypeDashboard.bidPublication' | t }}</th>
                  <th>{{ 'procTypeDashboard.submissionDate' | t }}</th>
                  <th>{{ 'procTypeDashboard.emdStatus' | t }}</th>
                  <th>{{ 'procTypeDashboard.bidStatus' | t }}</th>
                  <th>{{ 'procTypeDashboard.purchaseOrder' | t }}</th>
                  <th>{{ 'procTypeDashboard.deliveryAcceptance' | t }}</th>
                  <th>{{ 'procTypeDashboard.performanceSecurity' | t }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let item of pagedProcurementCases(procurementType)">
                  <td>{{ item.case_no }}</td>
                  <td>{{ dateText(item.bid_publication_date) }}</td>
                  <td>{{ dateText(item.submission_date) }}</td>
                  <td><span class="status">{{ item.emd_status | t }}</span></td>
                  <td><span class="status">{{ item.bid_status | t }}</span></td>
                  <td>
                    <span class="status">{{ item.purchase_order_status | t }}</span>
                    <small *ngIf="item.purchase_order_no">{{ item.purchase_order_no }}</small>
                  </td>
                  <td><span class="status">{{ item.delivery_acceptance_status | t }}</span></td>
                  <td><span class="status">{{ item.performance_security_status | t }}</span></td>
                </tr>
                <tr *ngIf="!procurementType.cases.length">
                  <td colspan="8" class="report-empty">{{ 'dashboard.noData' | t }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <app-pagination
            [total]="procurementType.cases.length"
            [page]="casePage"
            [pageSize]="casePageSize"
            [pageSizes]="tablePageSizes"
            (pageChange)="casePage = $event"
            (pageSizeChange)="changeCasePageSize($event)">
          </app-pagination>
        </ng-container>
      </section>

      <section class="panel req-detail-panel" *ngIf="activeBudget">
        <div class="section-heading compact-heading">
          <div>
            <h2>{{ activeBudget.budget_head | t }}</h2>
            <p>{{ 'reqDashboard.budgetSelectionMessage' | t }}</p>
          </div>
          <div class="req-budget-amount selected">
            <strong>{{ activeBudget.amount | lcurrency:'INR':'1.2-2' }}</strong>
            <span>{{ activeBudget.count | ln:'1.0-0' }} {{ 'common.records' | t }}</span>
          </div>
        </div>
      </section>

      <section class="panel req-recent-panel">
        <div class="section-heading compact-heading">
          <div>
            <h2>{{ 'dashboard.recentActivity' | t }}</h2>
            <p>{{ 'reqDashboard.recentDescription' | t }}</p>
          </div>
        </div>
        <div class="table-scroll req-recent-table">
          <table>
            <thead>
              <tr>
                <th>{{ 'Requisition No' | t }}</th>
                <th>{{ 'Received From' | t }}</th>
                <th>{{ 'Budget Head' | t }}</th>
                <th>{{ 'Estimated Cost' | t }}</th>
                <th>{{ 'Approval Status' | t }}</th>
                <th>{{ 'Updated' | t }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let item of pagedRecent">
                <td>{{ item.requisition_no }}</td>
                <td>{{ item.received_from || '-' }}</td>
                <td>{{ item.budget_head | t }}</td>
                <td>{{ item.amount | lcurrency:'INR':'1.2-2' }}</td>
                <td><span class="status">{{ item.approval_status | t | titlecase }}</span></td>
                <td>{{ item.updated_at | ldate }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <app-pagination
          [total]="data.recent.length"
          [page]="recentPage"
          [pageSize]="recentPageSize"
          [pageSizes]="cardPageSizes"
          (pageChange)="recentPage = $event"
          (pageSizeChange)="changeRecentPageSize($event)">
        </app-pagination>
      </section>
    </section>

    <ng-template #loadingState>
      <section class="panel">
        <p class="loading-text">{{ loading ? ('dashboard.loadingIndicators' | t) : ('dashboard.noData' | t) }}</p>
        <p class="error" *ngIf="error">{{ error }}</p>
      </section>
    </ng-template>
  `
})
export class RequisitionDashboardComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly i18n = inject(I18nService);

  data?: RequisitionDashboardData;
  procurementTypes: ProcurementTypeDashboard[] = [];
  activeStatus?: RequisitionDashboardStatus;
  activeBudget?: RequisitionBudgetHeadAmount;
  activeProcurementType?: ProcurementTypeDashboard;
  readonly cardPageSizes = [5, 10, 25];
  readonly tablePageSizes = [10, 25, 50, 100];
  budgetPage = 1;
  budgetPageSize = 5;
  casePage = 1;
  casePageSize = 10;
  recentPage = 1;
  recentPageSize = 5;
  loading = false;
  error = '';

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.api.requisitionDashboard().subscribe({
      next: (data) => {
        this.data = data;
        this.activeStatus = this.visibleStatuses[0];
        this.activeBudget = data.budget_heads[0];
        this.budgetPage = 1;
        this.recentPage = 1;
        this.error = '';
        this.loading = false;
      },
      error: (response) => {
        this.error = response?.error?.detail || 'Unable to load requisition dashboard.';
        this.loading = false;
      }
    });
    this.api.procurementTypeDashboards().subscribe({
      next: (data) => {
        this.procurementTypes = data.types;
        this.activeProcurementType = data.types[0];
        this.casePage = 1;
      },
      error: (response) => {
        this.error = response?.error?.detail || this.error || 'Unable to load procurement type dashboards.';
      }
    });
  }

  selectStatus(item: RequisitionDashboardStatus): void {
    this.activeStatus = item;
  }

  get visibleStatuses(): RequisitionDashboardStatus[] {
    return (this.data?.status || []).filter(item => item.key !== 'rejected');
  }

  selectBudget(item: RequisitionBudgetHeadAmount): void {
    this.activeBudget = item;
  }

  selectProcurementType(item: ProcurementTypeDashboard): void {
    this.activeProcurementType = item;
    this.casePage = 1;
  }

  get pagedBudgetHeads(): RequisitionBudgetHeadAmount[] {
    const items = this.data?.budget_heads || [];
    const start = (this.budgetPage - 1) * this.budgetPageSize;
    return items.slice(start, start + this.budgetPageSize);
  }

  pagedProcurementCases(item: ProcurementTypeDashboard): ProcurementTypeDashboard['cases'] {
    const start = (this.casePage - 1) * this.casePageSize;
    return item.cases.slice(start, start + this.casePageSize);
  }

  get pagedRecent(): RequisitionDashboardData['recent'] {
    const items = this.data?.recent || [];
    const start = (this.recentPage - 1) * this.recentPageSize;
    return items.slice(start, start + this.recentPageSize);
  }

  changeBudgetPageSize(pageSize: number): void {
    this.budgetPageSize = pageSize;
    this.budgetPage = 1;
  }

  changeCasePageSize(pageSize: number): void {
    this.casePageSize = pageSize;
    this.casePage = 1;
  }

  changeRecentPageSize(pageSize: number): void {
    this.recentPageSize = pageSize;
    this.recentPage = 1;
  }

  print(): void {
    window.print();
  }

  get statusShare(): number {
    const total = this.data?.summary.received || 0;
    return total && this.activeStatus ? (this.activeStatus.count / total) * 100 : 0;
  }

  get insightTitle(): string {
    if (!this.activeStatus) return 'reqDashboard.noStatusSelected';
    if (this.activeStatus.key === 'approved') return 'reqDashboard.approvedInsightTitle';
    if (this.activeStatus.key === 'rejected') return 'reqDashboard.rejectedInsightTitle';
    if (this.activeStatus.key === 'pending') return 'reqDashboard.pendingInsightTitle';
    return 'reqDashboard.receivedInsightTitle';
  }

  get insightText(): string {
    if (!this.activeStatus) return 'reqDashboard.noStatusSelected';
    if (this.activeStatus.key === 'approved') return 'reqDashboard.approvedInsightText';
    if (this.activeStatus.key === 'rejected') return 'reqDashboard.rejectedInsightText';
    if (this.activeStatus.key === 'pending') return 'reqDashboard.pendingInsightText';
    return 'reqDashboard.receivedInsightText';
  }

  budgetShare(amount: number): number {
    const total = this.data?.summary.total_amount || 0;
    return total ? Math.max(2, (amount / total) * 100) : 0;
  }

  nonEmptyCounts(items: DashboardCountItem[]): DashboardCountItem[] {
    return items.length ? items : [{ label: 'Not Recorded', count: 0 }];
  }

  publishedCount(item: ProcurementTypeDashboard): number {
    return item.cases.filter((row) => row.bid_publication_date).length;
  }

  submittedDateCount(item: ProcurementTypeDashboard): number {
    return item.cases.filter((row) => row.submission_date).length;
  }

  dateText(value?: string | null): string {
    return value ? this.i18n.formatDate(value) : this.i18n.t('Not Recorded');
  }

  procurementTypeRoute(key: string): string {
    const routes: Record<string, string> = {
      GEM: '/lifecycle/gem-cases',
      E_TENDER: '/lifecycle/e-tender-cases',
      GLOBAL_TENDER: '/lifecycle/global-tender-cases',
      LPC: '/lifecycle/lpc-cases',
      OPEN_MARKET: '/lifecycle/open-market-cases'
    };
    return routes[key] || '/lifecycle/procurement-cases';
  }

  procurementTypeMessage(item: ProcurementTypeDashboard): string {
    const notIssued = item.purchase_order_status.find((status) => status.label === 'Not Issued')?.count || 0;
    const notSubmittedEmd = item.emd_status.find((status) => status.label === 'Not Submitted')?.count || 0;
    const notRecordedDelivery = item.delivery_acceptance_status.find((status) => status.label === 'Not Recorded')?.count || 0;
    if (notIssued) return 'procTypeDashboard.poPendingMessage';
    if (notSubmittedEmd) return 'procTypeDashboard.emdPendingMessage';
    if (notRecordedDelivery) return 'procTypeDashboard.deliveryPendingMessage';
    return 'procTypeDashboard.healthyMessage';
  }
}
